import { Alert, Spin, Typography } from "antd";
import { useTranslation } from "react-i18next";

import { TAX_ADVISORY_PERMISSION_SCOPES, useAuth } from "@/modules/auth";
import { useUserContext } from "../../../../contexts/UserContext";
import { useQuestionnairesQuery } from "../../../../../../../api/graphql/queries/questionnaire/questionnaires.generated";
import { useTaxCaseQuery } from "../../../../../../../api/graphql/queries/taxCase/taxCase.generated";
import { Questionnaire } from "../../../../../../../api/graphql/schema.generated";
import EmptyWrapper from "../../../../../../common/EmptyWrapper";
import useEmailParam from "../../../../../../hooks/useEmailParam";
import useTaxYearParam from "../../../../hooks/useTaxYearParam";
import {
  getSupportedQuestionnairesInfoForYear,
  hasCompanyQuestionnaireRestriction,
  isShownOnlyIfMatch,
} from "../utils";
import useUnavailableQuestionnaireTypes from "../useUnavailableQuestionnaireTypes";
import { EOY_INCOME_TAX_QUESTIONNAIRE_TYPES } from "./constants";
import QuestionnaireCard from "./QuestionnaireCard";
import { CardsWrapper } from "./styledComponents";

const { Title } = Typography;

const FIRST_SUPPORTED_TAX_YEAR = 2021;

const TaxPreparationOverview = () => {
  const { t } = useTranslation("TaxCaseOverview", {
    keyPrefix: "companyHints",
  });
  const user = useUserContext();
  const [taxYear] = useTaxYearParam();
  const [email] = useEmailParam();

  const { hasOperatorPermissionScope } = useAuth();
  const hasTaxAdvisoryPermissionScope = hasOperatorPermissionScope(
    TAX_ADVISORY_PERMISSION_SCOPES
  );

  const isSupportedTaxYear = taxYear >= FIRST_SUPPORTED_TAX_YEAR;
  const { data: questionnairesData, loading: isLoadingQuestionnaires } =
    useQuestionnairesQuery({
      skip: !email || !isSupportedTaxYear,
      variables: {
        email: email!,
        year: taxYear,
      },
    });
  const questionnaires = questionnairesData?.questionnaires || [];

  const { data: taxCaseData, loading: isLoadingTaxCase } = useTaxCaseQuery({
    skip: !email || !isSupportedTaxYear || !hasTaxAdvisoryPermissionScope,
    variables: {
      email: email!,
      year: taxYear,
    },
  });
  const { unavailableTypes, loading: isLoadingUnavailableTypes } =
    useUnavailableQuestionnaireTypes({
      email,
      taxYear,
      skip: !isSupportedTaxYear,
    });

  const isIncomeTaxSkipped = !!taxCaseData?.taxCase?.incomeTaxSkippedAt;

  const isLoading = isLoadingQuestionnaires || isLoadingTaxCase;

  if (!email) {
    return <EmptyWrapper description="Suche nach einem Mandanten" />;
  }

  if (!isSupportedTaxYear) {
    return (
      <EmptyWrapper
        description={`Vorbereitung der Steuererklärung wird erst ab ${FIRST_SUPPORTED_TAX_YEAR} unterstützt`}
      />
    );
  }

  return (
    <>
      <Title
        level={3}
        className="mb-3"
      >{`Vorbereitung der Steuererklärung ${taxYear}`}</Title>

      {!isLoadingUnavailableTypes &&
        user?.companyType &&
        hasCompanyQuestionnaireRestriction(unavailableTypes) && (
          <Alert
            className="mb-3"
            type="info"
            showIcon
            message={t("questionnaires", { legalForm: user.companyType })}
          />
        )}

      {isLoadingUnavailableTypes ? (
        // Cards a company cannot fill in must not flash in and out.
        <Spin />
      ) : (
        <CardsWrapper>
          {getSupportedQuestionnairesInfoForYear(taxYear).map(
            (info, infoIndex) => {
              const matchedQuestionnaires = questionnaires.filter(
                ({ type }) => type === info.type
              );
              const isNotRelevant =
                isIncomeTaxSkipped &&
                EOY_INCOME_TAX_QUESTIONNAIRE_TYPES.includes(info.type);

              if (
                !matchedQuestionnaires.length &&
                !isShownOnlyIfMatch(info, unavailableTypes)
              ) {
                return (
                  <QuestionnaireCard
                    key={`${info.type}_${infoIndex}`}
                    taxYear={taxYear}
                    info={info}
                    questionnaire={undefined}
                    loading={isLoading}
                    isNotRelevant={isNotRelevant}
                  />
                );
              }

              return matchedQuestionnaires.map((questionnaire, subIndex) => (
                <QuestionnaireCard
                  key={`${info.type}_${infoIndex}_${subIndex}`}
                  taxYear={taxYear}
                  info={info}
                  questionnaire={questionnaire as Questionnaire}
                  loading={isLoading}
                  isNotRelevant={isNotRelevant}
                />
              ));
            }
          )}
        </CardsWrapper>
      )}
    </>
  );
};

export default TaxPreparationOverview;
